/**
 * ============================================================================
 * REMINDER SERVICE v1.1.0 - CAPACITOR SUPPORT
 * ============================================================================
 * Notification Service für Kalender-Erinnerungen
 *
 * Features:
 * - Browser Notifications (Web)
 * - Capacitor LocalNotifications (iOS/Android)
 * - Scheduled Reminders basierend auf reminder_minutes
 * - Persistenz der Reminder-Timer
 * - Sound-Benachrichtigung (optional)
 *
 * ✅ v1.1.0: Capacitor LocalNotifications Support
 *
 * @version 1.1.0
 * ============================================================================
 */

// Reminder-Optionen für das UI
export const reminderOptions = [
  { value: null, label: 'Keine Erinnerung' },
  { value: 5, label: '5 Minuten vorher' },
  { value: 10, label: '10 Minuten vorher' },
  { value: 15, label: '15 Minuten vorher' },
  { value: 30, label: '30 Minuten vorher' },
  { value: 60, label: '1 Stunde vorher' },
  { value: 120, label: '2 Stunden vorher' },
  { value: 1440, label: '1 Tag vorher' }
];

// Storage für aktive Timer (wird bei Reload verloren, aber beim Laden neu erstellt)
const activeTimers = new Map();

// ===========================
// ✅ v1.1: CAPACITOR DETECTION
// ===========================

/**
 * Prüft ob die App in Capacitor/Native läuft
 */
function isNativePlatform() {
  try {
    // Check for Capacitor
    const isCapacitor = !!(window.Capacitor && window.Capacitor.isNative);
    return isCapacitor;
  } catch (e) {
    return false;
  }
}

/**
 * Lädt LocalNotifications Plugin dynamisch
 */
async function getLocalNotificationsPlugin() {
  if (!isNativePlatform()) return null;

  try {
    const { LocalNotifications } = await import('@capacitor/local-notifications');
    return LocalNotifications;
  } catch (e) {
    console.warn('🔔 LocalNotifications plugin not available:', e.message);
    return null;
  }
}

// ===========================
// NOTIFICATION PERMISSION
// ===========================

/**
 * Prüft ob Notifications unterstützt werden (Browser oder Native)
 */
export function isNotificationSupported() {
  // Native: LocalNotifications Plugin
  if (isNativePlatform()) {
    return true; // Capacitor LocalNotifications ist immer verfügbar
  }
  // Web: Browser Notification API
  return 'Notification' in window;
}

/**
 * Prüft den aktuellen Permission-Status
 */
export async function getNotificationPermission() {
  if (!isNotificationSupported()) return 'unsupported';

  // ✅ v1.1: Capacitor LocalNotifications
  if (isNativePlatform()) {
    try {
      const LocalNotifications = await getLocalNotificationsPlugin();
      if (LocalNotifications) {
        const status = await LocalNotifications.checkPermissions();
        return status.display; // 'granted', 'denied', 'prompt'
      }
    } catch (e) {
      console.warn('🔔 LocalNotifications permission check failed:', e.message);
    }
    return 'default';
  }

  // Web: Browser Notification API
  return Notification.permission; // 'granted', 'denied', 'default'
}

/**
 * Fordert Notification-Berechtigung an
 * @returns {Promise<string>} Permission status
 */
export async function requestNotificationPermission() {
  if (!isNotificationSupported()) {
    console.warn('🔔 Notifications werden nicht unterstützt');
    return 'unsupported';
  }

  // ✅ v1.1: Capacitor LocalNotifications
  if (isNativePlatform()) {
    try {
      const LocalNotifications = await getLocalNotificationsPlugin();
      if (LocalNotifications) {
        // Prüfe aktuellen Status
        let status = await LocalNotifications.checkPermissions();
        console.log('🔔 Native permission status:', status.display);

        if (status.display === 'granted') {
          return 'granted';
        }

        if (status.display === 'denied') {
          console.warn('🔔 Native notifications denied - user needs to enable in settings');
          return 'denied';
        }

        // Permission anfragen
        status = await LocalNotifications.requestPermissions();
        console.log('🔔 Native permission after request:', status.display);
        return status.display;
      }
    } catch (error) {
      console.error('🔔 Native permission error:', error);
      return 'error';
    }
    return 'unsupported';
  }

  // Web: Browser Notification API
  if (Notification.permission === 'granted') {
    console.log('🔔 Notification permission already granted');
    return 'granted';
  }

  if (Notification.permission === 'denied') {
    console.warn('🔔 Notification permission was denied');
    return 'denied';
  }

  try {
    const permission = await Notification.requestPermission();
    console.log('🔔 Notification permission:', permission);
    return permission;
  } catch (error) {
    console.error('🔔 Error requesting notification permission:', error);
    return 'error';
  }
}

// ===========================
// SHOW NOTIFICATION
// ===========================

/**
 * Zeigt eine Notification an (Browser oder Native)
 * @param {string} title - Titel der Notification
 * @param {object} options - Notification-Optionen
 */
export async function showNotification(title, options = {}) {
  if (!isNotificationSupported()) {
    console.warn('🔔 Notifications not supported, using alert fallback');
    alert(`Erinnerung: ${title}`);
    return null;
  }

  // ✅ v1.1: Capacitor LocalNotifications
  if (isNativePlatform()) {
    try {
      const LocalNotifications = await getLocalNotificationsPlugin();
      if (LocalNotifications) {
        const notificationId = options.id || Math.floor(Math.random() * 100000);

        await LocalNotifications.schedule({
          notifications: [{
            id: notificationId,
            title: title,
            body: options.body || '',
            sound: 'default',
            smallIcon: 'ic_notification',
            largeIcon: 'ic_launcher',
            extra: options.data || {}
          }]
        });

        console.log('🔔 Native notification shown:', notificationId);
        return { id: notificationId };
      }
    } catch (error) {
      console.error('🔔 Native notification error:', error);
      return null;
    }
  }

  // Web: Browser Notification API
  if (Notification.permission !== 'granted') {
    console.warn('🔔 Notification permission not granted');
    return null;
  }

  const defaultOptions = {
    icon: '/favicon.ico',
    badge: '/favicon.ico',
    tag: 'calendar-reminder',
    requireInteraction: true,
    silent: false
  };

  const notification = new Notification(title, { ...defaultOptions, ...options });

  notification.onclick = () => {
    window.focus();
    notification.close();
    if (options.onClick) {
      options.onClick();
    }
  };

  // Auto-close nach 30 Sekunden
  setTimeout(() => notification.close(), 30000);

  return notification;
}

// ===========================
// SCHEDULE REMINDERS
// ===========================

/**
 * Berechnet die Zeit bis zur Erinnerung
 * @param {string} date - Datum im Format YYYY-MM-DD
 * @param {string} time - Zeit im Format HH:MM
 * @param {number} reminderMinutes - Minuten vor dem Termin
 * @returns {number} Millisekunden bis zur Erinnerung (negativ wenn bereits vorbei)
 */
export function calculateReminderDelay(date, time, reminderMinutes) {
  if (!date || !time || !reminderMinutes) return -1;

  // Parse Datum und Zeit
  const [year, month, day] = date.split('-').map(Number);
  const [hours, minutes] = time.split(':').map(Number);

  // Erstelle Termin-Zeitpunkt
  const appointmentTime = new Date(year, month - 1, day, hours, minutes, 0);

  // Berechne Erinnerungs-Zeitpunkt
  const reminderTime = new Date(appointmentTime.getTime() - reminderMinutes * 60 * 1000);

  // Berechne Differenz zu jetzt
  const now = new Date();
  const delay = reminderTime.getTime() - now.getTime();

  return delay;
}

/**
 * Plant eine Erinnerung für einen Termin
 * @param {object} appointment - Der Termin mit id, title, date, time, reminder_minutes
 * @returns {boolean} true wenn Erinnerung geplant wurde
 */
export function scheduleReminder(appointment) {
  const { id, title, date, time, reminder_minutes, category, person } = appointment;

  // Keine Erinnerung gewünscht oder fehlende Daten
  if (!reminder_minutes || !date || !time) {
    console.log(`🔔 No reminder for "${title}" (no reminder_minutes or missing date/time)`);
    return false;
  }

  // Bestehenden Timer löschen falls vorhanden
  cancelReminder(id);

  const delay = calculateReminderDelay(date, time, reminder_minutes);

  // Termin liegt in der Vergangenheit
  if (delay < 0) {
    console.log(`🔔 Reminder for "${title}" skipped (already past)`);
    return false;
  }

  // Zu weit in der Zukunft (max 24h, um Memory zu sparen)
  const maxDelay = 24 * 60 * 60 * 1000; // 24 Stunden
  if (delay > maxDelay) {
    console.log(`🔔 Reminder for "${title}" deferred (more than 24h away)`);
    return false;
  }

  console.log(`🔔 Scheduling reminder for "${title}" in ${Math.round(delay / 60000)} minutes`);

  const timerId = setTimeout(() => {
    // Entferne aus activeTimers
    activeTimers.delete(id);

    // Zeige Notification
    const reminderLabel = reminderOptions.find(r => r.value === reminder_minutes)?.label || `${reminder_minutes} Min vorher`;

    showNotification(`📅 ${title}`, {
      body: `Termin in ${reminderLabel.replace(' vorher', '')}!\n📅 ${formatDisplayDate(date)} um ${time}${person ? `\n👤 ${person}` : ''}`,
      tag: `reminder-${id}`,
      data: { appointmentId: id }
    });

    console.log(`🔔 Reminder triggered for "${title}"`);
  }, delay);

  // Speichere Timer-Referenz
  activeTimers.set(id, {
    timerId,
    appointment,
    triggerTime: new Date(Date.now() + delay)
  });

  return true;
}

/**
 * Storniert eine geplante Erinnerung
 * @param {string|number} appointmentId - ID des Termins
 */
export function cancelReminder(appointmentId) {
  const timer = activeTimers.get(appointmentId);
  if (timer) {
    clearTimeout(timer.timerId);
    activeTimers.delete(appointmentId);
    console.log(`🔔 Cancelled reminder for appointment ${appointmentId}`);
  }
}

/**
 * Storniert alle geplanten Erinnerungen
 */
export function cancelAllReminders() {
  activeTimers.forEach((timer, id) => {
    clearTimeout(timer.timerId);
  });
  activeTimers.clear();
  console.log('🔔 All reminders cancelled');
}

/**
 * Plant Erinnerungen für mehrere Termine
 * @param {Array} appointments - Array von Terminen
 * @returns {number} Anzahl der geplanten Erinnerungen
 */
export function scheduleRemindersForAppointments(appointments) {
  if (!appointments || !Array.isArray(appointments)) return 0;

  // Erst alle alten Timer löschen
  cancelAllReminders();

  let scheduledCount = 0;

  appointments.forEach(apt => {
    if (scheduleReminder(apt)) {
      scheduledCount++;
    }
  });

  console.log(`🔔 Scheduled ${scheduledCount} reminders for ${appointments.length} appointments`);
  return scheduledCount;
}

/**
 * Gibt alle aktiven Reminder zurück (für Debugging)
 */
export function getActiveReminders() {
  const reminders = [];
  activeTimers.forEach((timer, id) => {
    reminders.push({
      id,
      title: timer.appointment.title,
      triggerTime: timer.triggerTime,
      minutesLeft: Math.round((timer.triggerTime - new Date()) / 60000)
    });
  });
  return reminders.sort((a, b) => a.triggerTime - b.triggerTime);
}

// ===========================
// HELPER FUNCTIONS
// ===========================

/**
 * Formatiert Datum für Anzeige
 */
function formatDisplayDate(dateStr) {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-');
  return `${day}.${month}.${year}`;
}

/**
 * Initialisiert den ReminderService
 * Sollte beim App-Start aufgerufen werden
 */
export async function initReminderService() {
  const isNative = isNativePlatform();
  console.log('🔔 Initializing ReminderService v1.1', isNative ? '(Native/Capacitor)' : '(Web)');

  // Prüfe Support
  if (!isNotificationSupported()) {
    console.warn('🔔 Notifications werden nicht unterstützt');
    return { supported: false, permission: 'unsupported', isNative };
  }

  // Hole aktuellen Permission-Status (OHNE automatisch anzufragen!)
  const permission = await getNotificationPermission();

  console.log('🔔 ReminderService ready, permission:', permission);

  return {
    supported: true,
    permission,
    isNative
  };
}

export default {
  reminderOptions,
  isNotificationSupported,
  getNotificationPermission,
  requestNotificationPermission,
  showNotification,
  scheduleReminder,
  cancelReminder,
  cancelAllReminders,
  scheduleRemindersForAppointments,
  getActiveReminders,
  calculateReminderDelay,
  initReminderService
};
